import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Save, X, ArrowLeft, Loader2, AlertTriangle, CalendarDays, Users } from "lucide-react";
import { VENUE } from "../lib/venue";
import AdminSubscribers from "../components/AdminSubscribers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "thebar-admin-token";

// ---- datetime helpers ----
// Convert UTC ISO string back to a value for <input type="datetime-local"> (in browser local time)
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Convert datetime-local value to UTC ISO string for backend storage
const localInputToIso = (localStr) => {
  if (!localStr) return null;
  // `new Date("2025-12-12T21:00")` is parsed as local time
  const d = new Date(localStr);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const empty = () => ({
  title: "",
  type: "KARAOKE",
  description: "",
  fbUrl: "https://www.facebook.com/thebarcolac",
  start_date_time_local: "",
  end_date_time_local: "",
  performer: "",
  performer_type: "Other",
});

const fmtDisplay = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-AU", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Australia/Melbourne",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const fmtTimeOnly = (iso) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Australia/Melbourne",
    }).format(new Date(iso));
  } catch {
    return "";
  }
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // event id or "new"
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [tab, setTab] = useState("events");

  const headers = { "X-Admin-Token": token };

  const loadEvents = useCallback(async (t) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/events`, {
        headers: { "X-Admin-Token": t || token },
      });
      setEvents(r.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) verify(token);
    // eslint-disable-next-line
  }, []);

  const verify = async (t) => {
    try {
      await axios.post(`${API}/admin/auth/check`, {}, { headers: { "X-Admin-Token": t } });
      localStorage.setItem(LS_KEY, t);
      setAuthed(true);
      setAuthError("");
      await loadEvents(t);
    } catch (e) {
      localStorage.removeItem(LS_KEY);
      setAuthed(false);
      setAuthError("Wrong password. Try again.");
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    verify(token.trim());
  };

  const logout = () => {
    localStorage.removeItem(LS_KEY);
    setToken("");
    setAuthed(false);
  };

  const startNew = () => {
    setEditing("new");
    setForm(empty());
  };

  const startEdit = (ev) => {
    setEditing(ev.id);
    setForm({
      title: ev.title,
      type: ev.type,
      description: ev.description || "",
      fbUrl: ev.fbUrl,
      start_date_time_local: isoToLocalInput(ev.start_date_time),
      end_date_time_local: isoToLocalInput(ev.end_date_time),
      performer: ev.performer || "",
      performer_type: ev.performer_type || "Other",
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(empty());
  };

  const flash = (m) => {
    setMsg(m);
    setTimeout(() => setMsg(""), 2500);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.start_date_time_local) {
      flash("Start date/time is required.");
      return;
    }
    setSaving(true);
    try {
      const startIso = localInputToIso(form.start_date_time_local);
      let endIso = localInputToIso(form.end_date_time_local);
      if (!endIso && startIso) {
        // default to start + 4 hours
        const d = new Date(startIso);
        d.setHours(d.getHours() + 4);
        endIso = d.toISOString();
      }
      // If performer left blank, fall back to title for valid schema
      const performer = (form.performer && form.performer.trim()) || form.title;
      const performer_type = form.performer_type || "Other";
      const payload = {
        title: form.title,
        type: form.type,
        description: form.description,
        fbUrl: form.fbUrl,
        start_date_time: startIso,
        end_date_time: endIso,
        performer,
        performer_type,
      };
      if (editing === "new") {
        await axios.post(`${API}/admin/events`, payload, { headers });
        flash("Event created.");
      } else {
        await axios.put(`${API}/admin/events/${editing}`, payload, { headers });
        flash("Event updated.");
      }
      cancelEdit();
      await loadEvents();
    } catch (err) {
      console.error(err);
      flash("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/admin/events/${id}`, { headers });
      flash("Event deleted.");
      await loadEvents();
    } catch (e) {
      console.error(e);
      flash("Delete failed.");
    }
  };

  // -------- LOGIN VIEW --------
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#FDFBF7] flex items-center justify-center px-6">
        <form
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-[#111114] border border-white/10 p-8 sm:p-10"
        >
          <div className="flex items-center gap-3 mb-8">
            <img src={VENUE.logoUrl} alt="The Bar" className="h-12 w-12 object-contain" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#F5A623] font-semibold">
                The Bar
              </div>
              <div className="font-display text-2xl uppercase">Admin</div>
            </div>
          </div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[#FDFBF7]/60 font-semibold mb-2">
            Admin password
          </label>
          <input
            data-testid="admin-password-input"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            autoFocus
            className="w-full bg-black border border-white/15 focus:border-[#F5A623] outline-none px-4 py-3 text-[#FDFBF7]"
            placeholder="Enter the admin token"
          />
          {authError && (
            <p data-testid="admin-login-error" className="mt-3 text-sm text-red-400">
              {authError}
            </p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            className="mt-6 w-full bg-[#F5A623] hover:bg-amber-400 text-black font-bold uppercase tracking-wider px-6 py-3 transition-colors"
          >
            Sign In
          </button>
          <Link
            to="/"
            data-testid="admin-back-to-site"
            className="mt-4 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FDFBF7]/50 hover:text-[#F5A623]"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
        </form>
      </div>
    );
  }

  // -------- DASHBOARD VIEW --------
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FDFBF7]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 sticky top-0 bg-black/90 backdrop-blur z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={VENUE.logoUrl} alt="The Bar" className="h-9 w-9 object-contain" />
            <span className="font-display text-xl uppercase tracking-wider">The Bar Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="admin-view-site"
              className="text-xs uppercase tracking-[0.2em] text-[#FDFBF7]/70 hover:text-[#F5A623]"
            >
              View site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FDFBF7]/70 hover:text-[#F5A623]"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Tab switcher */}
        <div className="mb-10 border-b border-white/10 flex items-end gap-1" data-testid="admin-tabs">
          <TabButton
            active={tab === "events"}
            onClick={() => setTab("events")}
            icon={<CalendarDays size={16} />}
            testid="admin-tab-events"
          >
            Events
          </TabButton>
          <TabButton
            active={tab === "subscribers"}
            onClick={() => setTab("subscribers")}
            icon={<Users size={16} />}
            testid="admin-tab-subscribers"
          >
            Mailing List
          </TabButton>
        </div>

        {tab === "subscribers" ? (
          <AdminSubscribers token={token} onFlash={flash} />
        ) : (
          <EventsPane
            editing={editing}
            startNew={startNew}
            msg={msg}
            form={form}
            setForm={setForm}
            save={save}
            saving={saving}
            cancelEdit={cancelEdit}
            loading={loading}
            events={events}
            startEdit={startEdit}
            remove={remove}
          />
        )}
      </main>
    </div>
  );
}

// ------- Events tab body (kept inline for clarity) -------
function EventsPane({ editing, startNew, msg, form, setForm, save, saving, cancelEdit, loading, events, startEdit, remove }) {
  return (
    <>
        <div className="flex items-center justify-between mb-8">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-[#F5A623] font-semibold">
              / Upcoming Events
            </span>
            <h1 className="font-display text-4xl uppercase mt-2">Manage your bill</h1>
            <p className="text-sm text-[#FDFBF7]/55 mt-2 max-w-xl">
              All times shown in Melbourne local time. Past events automatically hide
              from the public site once the end time has passed.
            </p>
          </div>
          {editing === null && (
            <button
              onClick={startNew}
              data-testid="admin-new-event"
              className="inline-flex items-center gap-2 bg-[#F5A623] hover:bg-amber-400 text-black font-bold uppercase tracking-wider text-xs px-5 py-3"
            >
              <Plus size={16} /> New Event
            </button>
          )}
        </div>

        {msg && (
          <div
            data-testid="admin-flash"
            className="mb-6 border border-[#F5A623]/40 bg-[#F5A623]/10 text-[#F5A623] px-4 py-3 text-sm uppercase tracking-wider"
          >
            {msg}
          </div>
        )}

        {editing !== null && (
          <form
            onSubmit={save}
            data-testid="admin-event-form"
            className="bg-[#111114] border border-[#F5A623]/40 p-6 mb-8 grid sm:grid-cols-2 gap-4"
          >
            <div className="sm:col-span-2">
              <Label>Event title</Label>
              <Input
                testid="form-title"
                value={form.title}
                onChange={(v) => setForm({ ...form, title: v })}
                placeholder="Karaoke Friday: Christmas Edition"
                required
              />
            </div>

            <div>
              <Label>Start date & time *</Label>
              <Input
                testid="form-start"
                type="datetime-local"
                value={form.start_date_time_local}
                onChange={(v) => setForm({ ...form, start_date_time_local: v })}
                required
              />
            </div>
            <div>
              <Label>End date & time (optional)</Label>
              <Input
                testid="form-end"
                type="datetime-local"
                value={form.end_date_time_local}
                onChange={(v) => setForm({ ...form, end_date_time_local: v })}
              />
              <p className="text-[10px] text-[#FDFBF7]/40 mt-1 uppercase tracking-widest">
                Blank = start + 4 hrs
              </p>
            </div>

            <div>
              <Label>Type</Label>
              <select
                data-testid="form-type"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full bg-black border border-white/15 focus:border-[#F5A623] outline-none px-3 py-2.5 text-[#FDFBF7]"
              >
                <option value="KARAOKE">KARAOKE</option>
                <option value="LIVE MUSIC">LIVE MUSIC</option>
                <option value="PRIVATE EVENT">PRIVATE EVENT</option>
                <option value="SPECIAL">SPECIAL</option>
              </select>
            </div>
            <div>
              <Label>Performer type</Label>
              <select
                data-testid="form-performer-type"
                value={form.performer_type}
                onChange={(e) => setForm({ ...form, performer_type: e.target.value })}
                className="w-full bg-black border border-white/15 focus:border-[#F5A623] outline-none px-3 py-2.5 text-[#FDFBF7]"
              >
                <option value="MusicGroup">Band / Music group</option>
                <option value="Person">Solo performer / DJ</option>
                <option value="Other">Other (karaoke, open mic, etc.)</option>
              </select>
            </div>

            <div className="sm:col-span-2">
              <Label>Performer name</Label>
              <Input
                testid="form-performer"
                value={form.performer}
                onChange={(v) => setForm({ ...form, performer: v })}
                placeholder="e.g. The Lost Keys / DJ Mike / Open Karaoke"
              />
              <p className="text-[10px] text-[#FDFBF7]/40 mt-1 uppercase tracking-widest">
                Blank = event title used as performer for Google
              </p>
            </div>

            <div className="sm:col-span-2">
              <Label>Facebook event URL</Label>
              <Input
                testid="form-fburl"
                value={form.fbUrl}
                onChange={(v) => setForm({ ...form, fbUrl: v })}
                placeholder="https://www.facebook.com/events/..."
                required
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Description</Label>
              <textarea
                data-testid="form-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="w-full bg-black border border-white/15 focus:border-[#F5A623] outline-none px-3 py-2.5 text-[#FDFBF7] resize-none"
                placeholder="Short blurb shown beneath the title."
              />
            </div>
            <div className="sm:col-span-2 flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                data-testid="form-submit"
                className="inline-flex items-center gap-2 bg-[#F5A623] hover:bg-amber-400 disabled:opacity-60 text-black font-bold uppercase tracking-wider text-xs px-5 py-3"
              >
                <Save size={16} /> {editing === "new" ? "Create event" : "Save changes"}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                data-testid="form-cancel"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-white/40 text-[#FDFBF7] font-bold uppercase tracking-wider text-xs px-5 py-3"
              >
                <X size={16} /> Cancel
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="flex items-center gap-3 text-[#FDFBF7]/60 py-10">
            <Loader2 className="animate-spin" /> Loading…
          </div>
        ) : events.length === 0 ? (
          <div className="text-[#FDFBF7]/60 py-10" data-testid="admin-events-empty">
            No events yet. Hit &quot;New Event&quot; to add one.
          </div>
        ) : (
          <ul className="border border-white/10 divide-y divide-white/10">
            {events.map((ev) => {
              const now = Date.now();
              const isPast = ev.end_date_time && new Date(ev.end_date_time).getTime() < now;
              const isLegacy = !ev.start_date_time;
              return (
                <li
                  key={ev.id}
                  data-testid={`admin-event-row-${ev.id}`}
                  className={`flex items-center gap-4 p-4 ${isPast ? "bg-[#111114]/50 opacity-60" : "bg-[#111114]"}`}
                >
                  <div className="w-16 text-center border-r border-white/15 pr-3 shrink-0">
                    <div className="font-display text-3xl text-[#F5A623] leading-none">
                      {ev.date?.day || "??"}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-[#FDFBF7]/60 mt-1">
                      {ev.date?.month || "???"}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] uppercase tracking-widest text-[#F5A623] font-bold">
                      {ev.type} {isPast && "· PAST"}
                    </span>
                    <div className="font-display text-lg sm:text-xl uppercase truncate">{ev.title}</div>
                    <div className="text-xs text-[#FDFBF7]/55 mt-0.5">
                      {fmtDisplay(ev.start_date_time)}
                      {ev.end_date_time && ` – ${fmtTimeOnly(ev.end_date_time)}`}
                      {ev.performer && ` · ${ev.performer}`}
                    </div>
                    {isLegacy && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-400">
                        <AlertTriangle size={12} /> Needs proper start time
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => startEdit(ev)}
                    data-testid={`admin-edit-${ev.id}`}
                    className="p-2 text-[#FDFBF7]/70 hover:text-[#F5A623]"
                    aria-label="Edit"
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => remove(ev.id)}
                    data-testid={`admin-delete-${ev.id}`}
                    className="p-2 text-[#FDFBF7]/70 hover:text-red-400"
                    aria-label="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
    </>
  );
}

const Label = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#FDFBF7]/60 font-semibold mb-1">
    {children}
  </label>
);

const Input = ({ value, onChange, placeholder, type = "text", required, testid }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    required={required}
    data-testid={testid}
    className="w-full bg-black border border-white/15 focus:border-[#F5A623] outline-none px-3 py-2.5 text-[#FDFBF7]"
  />
);

const TabButton = ({ active, onClick, icon, testid, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em] font-semibold border-b-2 -mb-px transition-colors ${
      active
        ? "border-[#F5A623] text-[#F5A623]"
        : "border-transparent text-[#FDFBF7]/60 hover:text-[#FDFBF7]"
    }`}
  >
    {icon}
    {children}
  </button>
);
